import { useState, useEffect, useCallback } from 'react';
import Modal from 'react-bootstrap/Modal';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import { styled } from 'styled-components';
import moment from 'moment';
import { ModalButton } from '../../styles/StyledComponents';
import { getWorkTypes } from '../../services/OrdersService';
import { searchUsersByName } from '../../services/UsersService';
import { useAppContext } from '../../contextApi/context';
import CustomDatePicker from './CustomDatePicker';
import CustomHoursPicker from './CustomHoursPicker';
import { formatCurrency } from '../../utils/numbers';
import { formatDateForSending, parseDateForPicker } from '../../utils/dates';

/**
 * Modal para crear/editar servicios internos en órdenes de trabajo
 * @param {boolean} show - Controla la visibilidad del modal
 * @param {Function} onHide - Función para cerrar el modal
 * @param {Object|null} service - Servicio a editar (null para crear nuevo)
 * @param {Function} onSave - Función callback al guardar
 * @param {boolean} isSubmitting - Indica si se está guardando
 * @param {string} maintenanceStartDate - Fecha de inicio del mantenimiento de la orden
 */
const InternalServiceModal = ({ show, onHide, service, onSave, isSubmitting = false, maintenanceStartDate }) => {
    // Context
    const { loggedUser } = useAppContext();

    // State para los campos del formulario
    const [formData, setFormData] = useState({
        workTypeId: null,
        workSubtypeId: null,
        mechanicId: null,
        hoursSpent: '',
        totalAmount: '',
        startTime: '',
        endTime: '',
        description: '',
    });

    // State para catálogos
    const [workTypes, setWorkTypes] = useState([]);
    const [loadingWorkTypes, setLoadingWorkTypes] = useState(false);
    const [availableSubtypes, setAvailableSubtypes] = useState([]);

    // State para búsqueda de mecánicos (usuarios)
    const [availableMechanics, setAvailableMechanics] = useState([]);

    // Timer para búsqueda con debounce
    const [timer, setTimer] = useState(null);

    // State para errores de validación
    const [errors, setErrors] = useState({});

    const [apiError, setApiError] = useState(null);

    // State para el display formateado del monto total
    const [displayTotalAmount, setDisplayTotalAmount] = useState('');

    const [pickerResetKey, setPickerResetKey] = useState(0);

    // Estado para checkbox de confirmación de datos
    const [isDataConfirmed, setIsDataConfirmed] = useState(false);

    // Función helper para extraer fecha de datos del servidor (convierte a hora local primero)
    const getLocalDateOnly = (dateInput) => {
        if (!dateInput) return null;
        return moment.utc(dateInput).local().format('YYYY-MM-DD');
    };

    // Función helper para extraer fecha de datos del usuario (ya están en contexto correcto)
    const getDateOnly = (dateInput) => {
        if (!dateInput) return null;

        // Detectar si es fecha del picker (con zona horaria explícita) o del servidor
        const isPickerDate = typeof dateInput === 'string' && dateInput.includes('T') && dateInput.length > 19; // "2025-10-23T06:00:00Z" del picker

        const isServerDateOnly = typeof dateInput === 'string' && dateInput.match(/^\d{4}-\d{2}-\d{2}$/); // "2025-10-19" del servidor

        if (isServerDateOnly) {
            // Fecha solo servidor: usar moment directo
            return moment(dateInput).format('YYYY-MM-DD');
        } else if (isPickerDate) {
            // Fecha del picker: usar UTC→Local
            return moment.utc(dateInput).local().format('YYYY-MM-DD');
        } else {
            // Fecha servidor con hora: usar moment directo
            return moment(dateInput).format('YYYY-MM-DD');
        }
    };

    // Función para obtener tipos de trabajo desde la API
    const fetchWorkTypes = useCallback(async () => {
        if (!loggedUser?.token) return;

        setLoadingWorkTypes(true);
        try {
            const result = await getWorkTypes(loggedUser.token);

            if (result.success) {
                setWorkTypes(result.data);
            }
        } catch (error) {
            // Error al cargar tipos de trabajo
        } finally {
            setLoadingWorkTypes(false);
        }
    }, [loggedUser?.token]);

    // Cargar tipos de trabajo cuando se abre el modal
    useEffect(() => {
        if (show) {
            if (workTypes.length === 0) {
                fetchWorkTypes();
            }
        }
    }, [show, fetchWorkTypes, workTypes.length]);

    // Actualizar subtipos disponibles cuando cambia el tipo seleccionado
    useEffect(() => {
        if (formData.workTypeId) {
            const selectedType = workTypes.find((type) => type.id === formData.workTypeId);

            if (selectedType && selectedType.subtypes && Array.isArray(selectedType.subtypes)) {
                setAvailableSubtypes(selectedType.subtypes);
            } else {
                setAvailableSubtypes([]);
            }
        } else {
            setAvailableSubtypes([]);
            // Limpiar subtipo si se deselecciona el tipo
            setFormData((prev) => ({
                ...prev,
                workSubtypeId: null,
            }));
        }
    }, [formData.workTypeId, workTypes]);

    const calculateMaxHours = () => {
        if (formData.startTime && formData.endTime) {
            const start = moment.utc(formData.startTime).local().startOf('day');
            const end = moment.utc(formData.endTime).local().startOf('day');
            const diffInDays = end.diff(start, 'days') + 1;
            return diffInDays * 24;
        }
        return 168; // 7 días por defecto si no hay fechas
    };

    const handleInputChange = useCallback((field, value) => {
        setApiError(null);

        setFormData((prev) => ({
            ...prev,
            [field]: value,
        }));

        // Limpiar errores del campo que se está editando, EXCEPTO hoursSpent
        if (field !== 'hoursSpent') {
            setErrors((prev) => {
                if (prev[field]) {
                    return {
                        ...prev,
                        [field]: null,
                    };
                }
                return prev;
            });
        }
    }, []);

    // useEffect dedicado para validar horas trabajadas en tiempo real
    useEffect(() => {
        if (formData.hoursSpent && formData.hoursSpent !== '' && formData.hoursSpent !== '0') {
            const hoursSpent = parseFloat(formData.hoursSpent);

            if (formData.startTime && formData.endTime && !isNaN(hoursSpent)) {
                // Usar función inteligente para conversión de fechas
                const startDateOnly = getDateOnly(formData.startTime);
                const endDateOnly = getDateOnly(formData.endTime);

                // Crear momentos con solo fecha para comparar
                const startDate = moment(startDateOnly, 'YYYY-MM-DD');
                const endDate = moment(endDateOnly, 'YYYY-MM-DD');

                const diffInDays = endDate.diff(startDate, 'days') + 1; // +1 para incluir el día de inicio
                const maxHours = diffInDays * 24;

                if (hoursSpent > maxHours) {
                    setErrors((prev) => ({
                        ...prev,
                        hoursSpent: `No puede superar ${maxHours} horas (${diffInDays} día${diffInDays > 1 ? 's' : ''})`,
                    }));
                } else {
                    // Limpiar error si es válido
                    setErrors((prev) => ({
                        ...prev,
                        hoursSpent: null,
                    }));
                }
            }
        } else {
            // Si no hay horas trabajadas, limpiar cualquier error
            setErrors((prev) => ({
                ...prev,
                hoursSpent: null,
            }));
        }
    }, [formData.hoursSpent, formData.startTime, formData.endTime]);

    const hoursActionHandler = useCallback(
        (decimalValue) => {
            if (decimalValue === 0) {
                handleInputChange('hoursSpent', '');
            } else {
                handleInputChange('hoursSpent', decimalValue.toString());
            }
        },
        [handleInputChange]
    );

    // Manejo de selección de mecánico
    const handleSelectMechanic = (event, newValue) => {
        if (newValue != null) {
            handleAutocompleteChange('mechanicId', newValue);
        } else {
            setFormData((prev) => ({
                ...prev,
                mechanicId: null,
            }));
        }
    };

    // Búsqueda de mecánicos (usuarios) con debounce
    const handleSearchMechanics = (e, newValue, reason) => {
        clearTimeout(timer);

        const newTimer = setTimeout(async () => {
            if (reason === 'clear') {
                setAvailableMechanics([]);
                setFormData((prev) => ({
                    ...prev,
                    mechanicId: null,
                }));
            }

            if (newValue !== '') {
                if (reason === 'input') {
                    try {
                        const response = await searchUsersByName(loggedUser.token, newValue);

                        if (response && Array.isArray(response) && response.length > 0) {
                            setAvailableMechanics(
                                response.map((user) => ({
                                    id: user.id,
                                    label: `${user.firstName} ${user.lastName} (${user.username})`,
                                }))
                            );
                        } else {
                            setAvailableMechanics([]);
                        }
                    } catch (error) {
                        setAvailableMechanics([]);
                    }
                }
            }
        }, 500);

        setTimer(newTimer);
    };

    /**
     * Handler específico para el campo de monto total
     * Maneja la máscara visual y almacena el valor numérico real
     */
    const handleTotalAmountChange = (e) => {
        setApiError(null);

        // Obtener el valor ingresado
        let inputValue = e.target.value;

        // Remover todo excepto números y punto decimal (las comas son solo visuales)
        let numericValue = inputValue.replace(/[^\d.]/g, '');

        // Asegurar que solo haya un punto decimal
        const parts = numericValue.split('.');
        if (parts.length > 2) {
            numericValue = parts[0] + '.' + parts.slice(1).join('');
        }

        // Limitar a 2 decimales
        if (parts.length === 2 && parts[1].length > 2) {
            numericValue = parts[0] + '.' + parts[1].substring(0, 2);
        }

        // Actualizar el valor numérico real en formData
        setFormData((prev) => ({
            ...prev,
            totalAmount: numericValue,
        }));

        // Formatear para mostrar con máscara
        if (numericValue === '') {
            setDisplayTotalAmount('');
        } else if (numericValue === '.') {
            // Permitir escribir solo el punto para empezar con decimal
            setDisplayTotalAmount('0.');
        } else {
            // Si termina en punto, mantener el punto visible
            if (numericValue.endsWith('.')) {
                const numberValue = parseFloat(numericValue);
                if (!isNaN(numberValue)) {
                    setDisplayTotalAmount(formatCurrency(numberValue) + '.');
                } else {
                    setDisplayTotalAmount('0.');
                }
            } else {
                // Convertir a número y formatear normalmente
                const numberValue = parseFloat(numericValue);
                if (!isNaN(numberValue)) {
                    setDisplayTotalAmount(formatCurrency(numberValue));
                } else {
                    setDisplayTotalAmount('');
                }
            }
        }

        // Limpiar error del campo al empezar a escribir
        if (errors.totalAmount) {
            setErrors((prev) => ({
                ...prev,
                totalAmount: null,
            }));
        }
    };

    // Cargar datos del servicio cuando se abre en modo edición o limpiar cuando se cierra
    useEffect(() => {
        if (show) {
            if (service) {
                // Modo edición - cargar datos existentes
                setFormData({
                    workTypeId: service.workTypeId || null,
                    workSubtypeId: service.workSubtypeId || null,
                    mechanicId: service.mechanicId || null,
                    hoursSpent: service.hoursSpent !== null && service.hoursSpent !== undefined ? service.hoursSpent.toString() : '',
                    totalAmount: service.totalAmount ?? '',
                    startTime: service.startTime || '',
                    endTime: service.endTime || '',
                    description: service.description || '',
                });

                // Formatear el monto para mostrar con máscara
                if (service.totalAmount !== null && service.totalAmount !== undefined && service.totalAmount !== '') {
                    setDisplayTotalAmount(formatCurrency(service.totalAmount));
                } else {
                    setDisplayTotalAmount('');
                }

                // Si hay mecánico asignado, cargarlo en las opciones disponibles
                if (service.mechanicId && service.mechanicName) {
                    setAvailableMechanics([
                        {
                            id: service.mechanicId,
                            label: service.mechanicName,
                        },
                    ]);
                }
            } else {
                // Modo creación - limpiar formulario
                setFormData({
                    workTypeId: null,
                    workSubtypeId: null,
                    mechanicId: null,
                    hoursSpent: '',
                    totalAmount: '',
                    startTime: moment().format('YYYY-MM-DDTHH:mm:ss'),
                    endTime: '',
                    description: '',
                });
                setAvailableMechanics([]);
                setDisplayTotalAmount('');
            }

            setPickerResetKey((prev) => prev + 1);

            setErrors({});
            setApiError(null);
            setIsDataConfirmed(false);
        } else {
            // Limpiar todo cuando se cierra el modal
            setFormData({
                workTypeId: null,
                workSubtypeId: null,
                mechanicId: null,
                hoursSpent: '',
                totalAmount: '',
                startTime: '',
                endTime: '',
                description: '',
            });
            setAvailableMechanics([]);
            setDisplayTotalAmount('');
            setErrors({});
            setApiError(null);
            setIsDataConfirmed(false);

            setPickerResetKey((prev) => prev + 1);
        }
    }, [show, service]);

    // Manejo de cambio de fecha de inicio
    const handleStartTimeChange = (date) => {
        setApiError(null);

        setFormData((prev) => ({
            ...prev,
            startTime: date,
        }));

        // Validar que no sea fecha futura (comparando solo fechas en hora local)
        if (date) {
            const selectedDateOnly_OLD = getDateOnly(date);
            const currentDateOnly_OLD = moment().format('YYYY-MM-DD');

            if (selectedDateOnly_OLD > currentDateOnly_OLD) {
                setErrors((prev) => ({
                    ...prev,
                    startTime: 'No se puede seleccionar una fecha futura',
                }));
                return;
            }
        }

        // No puede ser anterior al inicio del mantenimiento (usando hora local para maintenanceStartDate)
        if (date && maintenanceStartDate) {
            const selectedDateOnly_OLD = getDateOnly(date);
            const maintenanceStartDateOnly_OLD = getLocalDateOnly(maintenanceStartDate);

            if (selectedDateOnly_OLD < maintenanceStartDateOnly_OLD) {
                const formattedMaintenanceStart = moment.utc(maintenanceStartDate).local().format('DD/MM/YYYY');
                setErrors((prev) => ({
                    ...prev,
                    startTime: `No puede ser anterior al inicio del mantenimiento (${formattedMaintenanceStart})`,
                }));
                return;
            }
        }

        // Si pasó todas las validaciones, limpiar error
        setErrors((prev) => ({
            ...prev,
            startTime: null,
        }));

        // Re-validar la fecha de fin si ya existe
        if (date && formData.endTime) {
            const startDateOnly_OLD = getDateOnly(date);
            const endDateOnly_OLD = getDateOnly(formData.endTime);

            if (endDateOnly_OLD < startDateOnly_OLD) {
                setErrors((prev) => ({
                    ...prev,
                    endTime: 'La fecha de fin no puede ser anterior a la de inicio',
                }));
            } else {
                setErrors((prev) => ({
                    ...prev,
                    endTime: null,
                }));

                // Re-validar horas trabajadas si ya existe
                if (formData.hoursSpent) {
                    const start = moment.utc(date).local().startOf('day');
                    const end = moment.utc(formData.endTime).local().startOf('day');
                    const diffInDays = end.diff(start, 'days') + 1;
                    const maxHours = diffInDays * 24;
                    const hoursSpent = parseFloat(formData.hoursSpent);

                    if (hoursSpent > maxHours) {
                        setErrors((prev) => ({
                            ...prev,
                            hoursSpent: `No puede superar ${maxHours} horas (${diffInDays} día${diffInDays > 1 ? 's' : ''})`,
                        }));
                    } else {
                        setErrors((prev) => ({
                            ...prev,
                            hoursSpent: null,
                        }));
                    }
                }
            }
        }
    };

    // Manejo de cambio de fecha de fin
    const handleEndTimeChange = (date) => {
        setApiError(null);

        setFormData((prev) => ({
            ...prev,
            endTime: date,
        }));

        // Validar que no sea fecha futura (comparando solo fechas en hora local)
        if (date) {
            const selectedDateOnly = getDateOnly(date);
            const currentDateOnly = moment().format('YYYY-MM-DD');

            if (selectedDateOnly > currentDateOnly) {
                setErrors((prev) => ({
                    ...prev,
                    endTime: 'No se puede seleccionar una fecha futura',
                }));
                return;
            }
        }

        // Validación en tiempo real
        if (date && formData.startTime) {
            const startDateOnly = getDateOnly(formData.startTime);
            const endDateOnly = getDateOnly(date);

            if (endDateOnly < startDateOnly) {
                setErrors((prev) => ({
                    ...prev,
                    endTime: 'La fecha de fin no puede ser anterior a la de inicio',
                }));
                return;
            } else {
                if (formData.hoursSpent) {
                    const start = moment.utc(formData.startTime).local().startOf('day');
                    const end = moment.utc(date).local().startOf('day');
                    const diffInDays = end.diff(start, 'days') + 1;
                    const maxHours = diffInDays * 24;
                    const hoursSpent = parseFloat(formData.hoursSpent);

                    if (hoursSpent > maxHours) {
                        setErrors((prev) => ({
                            ...prev,
                            hoursSpent: `No puede superar ${maxHours} horas (${diffInDays} día${diffInDays > 1 ? 's' : ''})`,
                        }));
                    } else {
                        setErrors((prev) => ({
                            ...prev,
                            hoursSpent: null,
                        }));
                    }
                }
            }
        } else {
            setErrors((prev) => ({
                ...prev,
                hoursSpent: null,
            }));
        }

        // Limpiar error del campo
        if (errors.endTime) {
            setErrors((prev) => ({
                ...prev,
                endTime: null,
            }));
        }
    };

    // Handler para cambios en Autocomplete
    const handleAutocompleteChange = (field, newValue) => {
        setApiError(null);

        const valueId = newValue?.id || null;
        setFormData((prev) => ({
            ...prev,
            [field]: valueId,
        }));

        // TODO: En el futuro, cuando haya relación entre tipos y subtipos, descomentar:
        // Si se cambia el Tipo de servicio, limpiar el subtipo
        // if (field === 'workTypeId') {
        //     setFormData((prev) => ({
        //         ...prev,
        //         workSubtypeId: null,
        //     }));
        // }

        // Limpiar error del campo
        if (errors[field]) {
            setErrors((prev) => ({
                ...prev,
                [field]: null,
            }));
        }
    };

    // Validar formulario antes de guardar
    const validateForm = () => {
        const newErrors = {};

        if (!formData.workTypeId) {
            newErrors.workTypeId = 'Campo requerido';
        }

        if (!formData.workSubtypeId) {
            newErrors.workSubtypeId = 'Campo requerido';
        }

        if (!formData.mechanicId) {
            newErrors.mechanicId = 'Campo requerido';
        }

        if (!formData.startTime) {
            newErrors.startTime = 'Campo requerido';
        } else {
            // Validar que no sea fecha futura (comparando solo fechas en hora local)
            const selectedDateOnly = getDateOnly(formData.startTime);
            const currentDateOnly = moment().format('YYYY-MM-DD');

            if (selectedDateOnly > currentDateOnly) {
                newErrors.startTime = 'No se puede seleccionar una fecha futura';
            }

            // Validar que no sea anterior a la fecha de inicio del mantenimiento (usando hora local)
            if (maintenanceStartDate) {
                const maintenanceStartDateOnly = getLocalDateOnly(maintenanceStartDate);

                if (selectedDateOnly < maintenanceStartDateOnly) {
                    newErrors.startTime = 'No puede ser anterior al inicio del mantenimiento';
                }
            }
        }

        // Validar fecha de fin solo si se proporcionó
        if (formData.endTime) {
            // Validar que no sea fecha futura (comparando solo fechas en hora local)
            const selectedDateOnly = getDateOnly(formData.endTime);
            const currentDateOnly = moment().format('YYYY-MM-DD');

            if (selectedDateOnly > currentDateOnly) {
                newErrors.endTime = 'No se puede seleccionar una fecha futura';
            } else if (formData.startTime) {
                // Validar que endTime sea posterior o igual a startTime
                const startDateOnly = getDateOnly(formData.startTime);
                const endDateOnly = getDateOnly(formData.endTime);

                if (endDateOnly < startDateOnly) {
                    newErrors.endTime = 'La fecha de fin no puede ser anterior a la de inicio';
                }
            }
        }

        // Validar horas trabajadas
        if (formData.hoursSpent && formData.hoursSpent !== '' && formData.hoursSpent !== '0') {
            const hoursSpent = parseFloat(formData.hoursSpent);

            if (isNaN(hoursSpent) || hoursSpent <= 0) {
                newErrors.hoursSpent = 'Debe ser un número mayor a 0';
            } else if (formData.startTime && formData.endTime) {
                const startDateOnly = getDateOnly(formData.startTime);
                const endDateOnly = getDateOnly(formData.endTime);
                const startDate = moment(startDateOnly, 'YYYY-MM-DD');
                const endDate = moment(endDateOnly, 'YYYY-MM-DD');
                const diffInDays = endDate.diff(startDate, 'days') + 1; // +1 para incluir el día de inicio
                const maxHours = diffInDays * 24; // Máximo 24 horas por día

                if (hoursSpent > maxHours) {
                    newErrors.hoursSpent = `No puede superar ${maxHours} horas (${diffInDays} día${diffInDays > 1 ? 's' : ''})`;
                }
            }
        }

        // Validar monto total solo si se proporciona
        if (formData.totalAmount) {
            const totalAmount = parseFloat(formData.totalAmount);

            if (isNaN(totalAmount) || totalAmount <= 0) {
                newErrors.totalAmount = 'Debe ser un número mayor a 0';
            }
        }

        if (!formData.description || formData.description.trim() === '') {
            newErrors.description = 'Campo requerido';
        } else if (formData.description.trim().length < 3) {
            newErrors.description = 'Mínimo 3 caracteres';
        } else if (formData.description.length > 300) {
            newErrors.description = 'Máximo 300 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Handler para guardar - Transforma las fechas a formato de fecha solamente
    const handleSave = () => {
        setApiError(null);

        if (validateForm()) {
            const dataToSend = {
                ...formData,
                // Usar la nueva función utilitaria
                startTime: formatDateForSending(formData.startTime),
                endTime: formatDateForSending(formData.endTime),
            };

            onSave(dataToSend, setApiError);
        }
    };

    return (
        <Modal show={show} onHide={onHide} size='lg' backdrop='static' centered>
            <Modal.Header className='fw-bold' closeButton>
                {service ? 'Editar Servicio Interno' : 'Agregar Servicio Interno'}
            </Modal.Header>
            <Modal.Body>
                {apiError && (
                    <ApiErrorAlert className='alert alert-danger text-center mb-3' role='alert'>
                        {apiError}
                    </ApiErrorAlert>
                )}

                <ModalFormGrid>
                    {/* Tipo de servicio */}
                    <div>
                        <label className='mb-1'>Tipo de servicio *</label>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={workTypes.map((type) => ({
                                id: type.id,
                                label: type.name,
                            }))}
                            value={
                                formData.workTypeId
                                    ? workTypes
                                          .map((type) => ({
                                              id: type.id,
                                              label: type.name,
                                          }))
                                          .find((type) => type.id === formData.workTypeId) || null
                                    : null
                            }
                            renderInput={(params) => <TextField {...params} label='Seleccionar tipo' error={!!errors.workTypeId} />}
                            onChange={(event, newValue) => handleAutocompleteChange('workTypeId', newValue)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText='No hay tipos disponibles'
                            disabled={isSubmitting || loadingWorkTypes}
                            loading={loadingWorkTypes}
                            sx={autocompleteIconStyles}
                        />
                        {errors.workTypeId && <ErrorText>{errors.workTypeId}</ErrorText>}
                    </div>

                    {/* Subtipo de servicio */}
                    <div>
                        <label className='mb-1'>Subtipo de servicio *</label>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={availableSubtypes.map((subtype) => ({
                                id: subtype.id,
                                label: subtype.name,
                            }))}
                            value={
                                formData.workSubtypeId
                                    ? availableSubtypes
                                          .map((subtype) => ({
                                              id: subtype.id,
                                              label: subtype.name,
                                          }))
                                          .find((subtype) => subtype.id === formData.workSubtypeId) || null
                                    : null
                            }
                            renderInput={(params) => <TextField {...params} label='Seleccionar subtipo' error={!!errors.workSubtypeId} />}
                            onChange={(event, newValue) => handleAutocompleteChange('workSubtypeId', newValue)}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText='Seleccione primero un Tipo de servicio'
                            disabled={isSubmitting || !formData.workTypeId}
                            sx={autocompleteIconStyles}
                        />
                        {errors.workSubtypeId && <ErrorText>{errors.workSubtypeId}</ErrorText>}
                    </div>

                    {/* Mecánico */}
                    <div>
                        <label className='mb-1'>Mecánico *</label>
                        <Autocomplete
                            disablePortal
                            size='small'
                            options={availableMechanics}
                            value={
                                formData.mechanicId && availableMechanics.length > 0
                                    ? availableMechanics.find((mechanic) => mechanic.id === formData.mechanicId) || null
                                    : null
                            }
                            renderInput={(params) => (
                                <TextField {...params} label='Buscar por nombre, apellido o usuario' error={!!errors.mechanicId} />
                            )}
                            onChange={handleSelectMechanic}
                            onInputChange={handleSearchMechanics}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            noOptionsText='Buscar por nombre, apellido o usuario'
                            disabled={isSubmitting}
                            sx={autocompleteIconStyles}
                        />
                        {errors.mechanicId && <ErrorText>{errors.mechanicId}</ErrorText>}
                    </div>

                    {/* Monto total */}
                    <div>
                        <label className='mb-1'>Monto total (₡)</label>
                        <input
                            type='text'
                            className={`form-control form-control-sm ${errors.totalAmount ? 'is-invalid' : ''}`}
                            placeholder='0.00'
                            value={displayTotalAmount}
                            onChange={handleTotalAmountChange}
                            disabled={isSubmitting}
                        />
                        {errors.totalAmount && <ErrorText>{errors.totalAmount}</ErrorText>}
                    </div>

                    {/* Fecha de inicio */}
                    <div>
                        <label className='mb-1'>Fecha de inicio *</label>
                        <CustomDatePicker
                            key={`start-${service?.id || 'new'}-${show}`}
                            defaultDate={
                                service?.startTime
                                    ? parseDateForPicker(service.startTime)
                                    : formData.startTime
                                    ? parseDateForPicker(formData.startTime)
                                    : moment()
                            }
                            minDate={maintenanceStartDate ? moment.utc(maintenanceStartDate).local() : null}
                            maxDate={moment()}
                            actionHandler={handleStartTimeChange}
                        />
                        {errors.startTime && <ErrorText>{errors.startTime}</ErrorText>}
                    </div>

                    {/* Fecha de finalización */}
                    <div>
                        <label className='mb-1'>Fecha de finalización</label>
                        <CustomDatePicker
                            key={`end-${service?.id || 'new'}-${show}`}
                            defaultDate={service?.endTime ? parseDateForPicker(service.endTime) : null}
                            minDate={formData.startTime ? parseDateForPicker(formData.startTime) : null}
                            maxDate={moment()}
                            actionHandler={handleEndTimeChange}
                        />
                        {errors.endTime && <ErrorText>{errors.endTime}</ErrorText>}
                    </div>

                    {/* Horas trabajadas */}
                    <div>
                        <label className='mb-1'>Horas trabajadas</label>
                        <CustomHoursPicker
                            key={`hours-picker-${pickerResetKey}-${formData.startTime}-${formData.endTime}`}
                            actionHandler={hoursActionHandler}
                            defaultValue={
                                service && formData.hoursSpent && formData.hoursSpent !== '' && !isNaN(parseFloat(formData.hoursSpent))
                                    ? parseFloat(formData.hoursSpent)
                                    : null
                            }
                            maxHours={calculateMaxHours()}
                            disabled={isSubmitting}
                        />
                        {errors.hoursSpent && <ErrorText>{errors.hoursSpent}</ErrorText>}
                    </div>

                    {/* Descripción */}
                    <div style={{ gridColumn: '1 / -1' }}>
                        <div className='d-flex justify-content-between align-items-center mb-1'>
                            <label className='mb-0'>Descripción *</label>
                            <CharacterCount isError={formData.description.length > 300}>{formData.description.length}/300</CharacterCount>
                        </div>
                        <textarea
                            className={`form-control form-control-sm ${errors.description ? 'is-invalid' : ''}`}
                            placeholder='Describe el trabajo realizado...'
                            rows='3'
                            maxLength='300'
                            value={formData.description}
                            onChange={(e) => handleInputChange('description', e.target.value)}
                            disabled={isSubmitting}
                        />
                        {errors.description && <ErrorText>{errors.description}</ErrorText>}
                    </div>
                </ModalFormGrid>

                {/* Checkbox de confirmación de datos - Solo en modo crear */}
                {!service && (
                    <ConfirmationCheckboxContainer>
                        <label className='checkbox-wrapper'>
                            <input
                                type='checkbox'
                                id='confirmData'
                                checked={isDataConfirmed}
                                onChange={(e) => setIsDataConfirmed(e.target.checked)}
                                disabled={isSubmitting}
                            />
                            <span className='checkmark'></span>
                            <span className='checkbox-label'>Confirmo que todos los datos digitados son correctos</span>
                        </label>
                        <span className='checkbox-hint'>Verifique que toda la información del servicio interno sea correcta antes de crear</span>
                    </ConfirmationCheckboxContainer>
                )}
            </Modal.Body>
            <Modal.Footer>
                <ModalButton className='cancel' onClick={onHide} disabled={isSubmitting}>
                    Cancelar
                </ModalButton>
                <ModalButton className='action' onClick={handleSave} disabled={isSubmitting || (!service ? !isDataConfirmed : false)}>
                    {isSubmitting ? (
                        <>
                            <span className='spinner-border spinner-border-sm me-2' role='status' aria-hidden='true'></span>
                            Guardando...
                        </>
                    ) : service ? (
                        'Guardar cambios'
                    ) : (
                        'Crear servicio'
                    )}
                </ModalButton>
            </Modal.Footer>
        </Modal>
    );
};

// Styled Components
const ModalFormGrid = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 15px;
    margin-bottom: 20px;

    > div {
        display: flex;
        flex-direction: column;

        label {
            font-size: 14px;
            font-weight: 600;
            color: #495057;
        }

        input,
        textarea,
        .MuiAutocomplete-root {
            width: 100%;
        }

        input[type='number'],
        input[type='text'] {
            height: 40px;
            padding: 8.5px 14px;
            font-size: 16px;
        }

        .MuiTextField-root {
            width: 100%;

            .MuiInputBase-root {
                height: 40px;
            }
        }

        textarea {
            resize: vertical;
            min-height: 80px;
        }
    }

    @media (max-width: 768px) {
        grid-template-columns: 1fr;
    }
`;

const ErrorText = styled.span`
    color: #d04437;
    font-size: 12px;
    margin-top: 4px;
`;

const CharacterCount = styled.span`
    font-size: 12px;
    color: ${(props) => (props.isError ? '#d04437' : '#6c757d')};
    font-weight: 500;
`;

const ApiErrorAlert = styled.div``;

const ConfirmationCheckboxContainer = styled.div`
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 4px;
    padding: 15px;
    margin-top: 0;
    margin-bottom: 0;

    .checkbox-wrapper {
        display: flex;
        align-items: flex-start;
        cursor: pointer;
        position: relative;
        user-select: none;
        gap: 12px;

        input[type='checkbox'] {
            position: absolute;
            opacity: 0;
            cursor: pointer;
            height: 0;
            width: 0;

            &:checked ~ .checkmark {
                background-color: #d04437;
                border-color: #d04437;

                &:after {
                    display: block;
                }
            }

            &:focus ~ .checkmark {
                box-shadow: 0 0 0 0.2rem rgba(208, 68, 55, 0.25);
            }

            &:disabled ~ .checkmark {
                background-color: #e9ecef;
                border-color: #ced4da;
                cursor: not-allowed;
            }

            &:disabled ~ .checkbox-label {
                color: #6c757d;
                cursor: not-allowed;
            }
        }

        .checkmark {
            position: relative;
            height: 20px;
            width: 20px;
            min-width: 20px;
            background-color: #fff;
            border: 2px solid #ced4da;
            border-radius: 4px;
            transition: all 0.2s ease;
            margin-top: 2px;

            &:after {
                content: '';
                position: absolute;
                display: none;
                left: 5px;
                top: 1px;
                width: 6px;
                height: 11px;
                border: solid white;
                border-width: 0 2px 2px 0;
                transform: rotate(45deg);
            }
        }

        .checkbox-label {
            font-size: 15px;
            font-weight: 500;
            color: #495057;
            line-height: 1.4;
        }

        &:hover .checkmark {
            border-color: #d04437;
        }
    }

    .checkbox-hint {
        display: block;
        font-size: 13px;
        color: #6c757d;
        margin-top: 8px;
        margin-left: 32px;
        line-height: 1.4;
    }
`;

const autocompleteIconStyles = {
    '& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator': {
        outline: 'none',
        backgroundColor: 'transparent',
        '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
        '&:focus': {
            outline: 'none',
            backgroundColor: 'transparent',
        },
        '&:focus-visible': {
            outline: 'none',
            backgroundColor: 'transparent',
        },
    },
    '& .MuiInputLabel-root': {
        fontWeight: 'normal',
    },
};

export default InternalServiceModal;
